import format from 'date-fns/format';

import { typeLabel } from '../constants/paymentLinks.constants';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  getEventLabel,
  getStatusConfig,
  getStatusDescription,
  sanitizeFilename,
} from './paymentLinks.utils';

const triggerDownload = (href: string, filename: string, revoke: boolean) => {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  if (revoke) URL.revokeObjectURL(href);
};

// Exporta a lista carregada como CSV (BOM + ";" para abrir no Excel pt-BR).
export const exportLinksCsv = (links: any[]) => {
  if (!links.length) return;

  const headers = [
    'Status',
    'Tipo',
    'Descrição',
    'Destino',
    'Valor',
    'Referência externa',
    'Vencimento',
    'Criado em',
    'Link',
  ];

  const escape = (value: any) => {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const rows = links.map((item) =>
    [
      getStatusConfig(item.status).label,
      typeLabel[item.type] || item.type,
      item.description,
      item.destination,
      Number(item.amount || 0)
        .toFixed(2)
        .replace('.', ','),
      item.external_reference,
      item.due_date ? formatDate(item.due_date) : '',
      item.created_at ? formatDateTime(item.created_at) : '',
      item.link_url,
    ]
      .map(escape)
      .join(';')
  );

  const lines = [headers.map(escape).join(';'), ...rows].join('\r\n');
  const blob = new Blob([`\uFEFF${lines}`], {
    type: 'text/csv;charset=utf-8;',
  });
  triggerDownload(
    URL.createObjectURL(blob),
    `links-pagamento-${format(new Date(), 'yyyy-MM-dd')}.csv`,
    true
  );
};

// Gera um .txt com o resumo do link. Retorna false quando não há item.
export const downloadLinkSummaryFile = (item: any): boolean => {
  if (!item) return false;

  const events = Array.isArray(item.events) ? item.events : [];
  const lines = [
    'Resumo do link de pagamento',
    '',
    `Status: ${getStatusConfig(item.status).label}`,
    `Situacao: ${getStatusDescription(item.status)}`,
    `Tipo: ${typeLabel[item.type] || item.type || '-'}`,
    `Valor: ${formatMoney(item.amount)}`,
    `Descricao: ${item.description || '-'}`,
    `Referencia externa: ${item.external_reference || '-'}`,
    `Codigo do link: ${item.reference_id || '-'}`,
    `Codigo da cobranca: ${item.charge_reference || '-'}`,
    `Codigo da transacao: ${item.checkout_reference_id || '-'}`,
    `Vencimento: ${formatDate(item.due_date)}`,
    `Criado em: ${formatDateTime(item.created_at)}`,
    `Aberto em: ${formatDateTime(item.opened_at)}`,
    `Enviado em: ${formatDateTime(item.sent_at)}`,
    `Pago em: ${formatDateTime(item.paid_at)}`,
    `Rejeitado em: ${formatDateTime(item.rejected_at)}`,
    `Ultima conferencia: ${formatDateTime(item.last_checked_at)}`,
    '',
    'Envio e pagador',
    `Canal: ${item.destination_type || '-'}`,
    `Destino: ${item.destination || '-'}`,
    `Nome: ${item.payer_name || '-'}`,
    `Documento: ${item.payer_document || '-'}`,
    `Email: ${item.payer_email || '-'}`,
    `Telefone: ${item.payer_phone || '-'}`,
    '',
    'Links',
    `Link curto: ${item.short_url || '-'}`,
    `Link completo: ${item.link_url || '-'}`,
    '',
    'Historico',
    ...(events.length
      ? events.map(
          (event) =>
            `${formatDateTime(event.created_at)} | ${getEventLabel(
              event.event_type
            )} | ${getStatusConfig(event.status).label} | ${
              event.message || '-'
            }`
        )
      : ['Sem eventos registrados.']),
  ];

  const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], {
    type: 'text/plain;charset=utf-8;',
  });
  const reference =
    item.external_reference || item.reference_id || item.short_code || item.id;
  triggerDownload(
    URL.createObjectURL(blob),
    `resumo-link-${sanitizeFilename(reference)}.txt`,
    true
  );
  return true;
};

// Baixa o QR renderizado (canvas) como PNG. Retorna false quando o canvas não existe.
export const downloadLinkQrCodeImage = (item: any): boolean => {
  const canvas = document.getElementById(
    'payment-link-qr'
  ) as HTMLCanvasElement | null;
  if (!canvas) return false;

  const reference =
    item?.external_reference ||
    item?.reference_id ||
    item?.short_code ||
    item?.id ||
    'link';
  triggerDownload(
    canvas.toDataURL('image/png'),
    `qrcode-${sanitizeFilename(reference)}.png`,
    false
  );
  return true;
};
